import { roundTo } from '@optimization/ssi-common';
import {
  type GetBatterySummaryArguments,
  type GetBatterySummaryResponseVM,
} from '@/app/services/solution/types/get-battery-summary';

interface BatteryLifeParameters {
  operatingDaysPerYear: number;
}

const MAX_OPERATING_DAYS = 365;
const MAX_YEARS = 12;
const MIN_YEARS = 1;

export const getBatteryLife = ({ operatingDaysPerYear }: BatteryLifeParameters): number => {
  const clampedDays = Math.max(0, Math.min(operatingDaysPerYear, MAX_OPERATING_DAYS));

  const ratio = clampedDays / MAX_OPERATING_DAYS;
  const years = MAX_YEARS * (1 - ratio);

  return Math.max(MIN_YEARS, Math.round(years));
};

const DEFAULT_DAILY_DISTANCE_KM = 400;
const DEFAULT_ENERGY_PER_KM_KWH = 1.25;
const DEGRADATION_PER_YEAR = 0.025;
const MIN_STATE_OF_HEALTH = 0.7;

const getBatterySummary = (batterySummaryArguments: GetBatterySummaryArguments): GetBatterySummaryResponseVM => {
  const batteryLife = getBatteryLife({
    operatingDaysPerYear: batterySummaryArguments.operatingDaysPerYear,
  });
  const yearsOfOwnership = Math.min(batterySummaryArguments.yearsOfOwnership, batteryLife);

  const yearlyDistanceKm = batterySummaryArguments.operatingDaysPerYear * DEFAULT_DAILY_DISTANCE_KM;
  const cycledEnergyKwh = yearlyDistanceKm * DEFAULT_ENERGY_PER_KM_KWH * yearsOfOwnership;
  const cycledEnergyMwh = cycledEnergyKwh / 1000;

  const rawStateOfHealth = 1 - yearsOfOwnership * DEGRADATION_PER_YEAR;
  const stateOfHealth = Math.max(rawStateOfHealth, MIN_STATE_OF_HEALTH);
  const stateOfHealthPercent = stateOfHealth * 100;

  return {
    yearsOfOwnership: roundTo(yearsOfOwnership, 1),
    operatingDaysPerYear: batterySummaryArguments.operatingDaysPerYear,
    stateOfHealthPercent: Math.round(stateOfHealthPercent),
    cycledEnergyMWh: Math.round(cycledEnergyMwh),
    yearlyDistanceKm: Math.round(yearlyDistanceKm),
    batteryLife,
  };
};

export default getBatterySummary;
